'use client';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactElement, ReactNode, useId } from 'react';
import BiometricConfidenceLabel from './BiometricConfidenceLabel';
import BiometricConfidenceMeter from './BiometricConfidenceMeter';
import { biometricConfidenceStatement } from './statements';
import { TBiometricConfidenceLevel } from './types';

export type TBiometricEvidenceDialogProps = {
  open: boolean;
  onClose: () => void;
  level: TBiometricConfidenceLevel;
  /** The heading, for example `Biometric checks`. Not named `title`: Storybook reserves that. */
  heading: string;
  /**
   * Overrides the status line beside the meter, which otherwise renders `level`'s own name via
   * `BiometricConfidenceLabel`.
   *
   * Exists for `IdPortraitEvidence`, whose heading reads "ID portrait" rather than "Biometric
   * checks". Against that heading the level-derived label at `NotCompleted` ("Not completed") reads
   * as the *portrait* being incomplete, when the portrait is present (it is the reason the dialog
   * is open at all) and it is the biometric check that has not been completed. Every other caller
   * leaves this unset and keeps the level-derived label, so default behaviour is unchanged.
   */
  statusOverride?: string;
  /** The captures, laid out beside the information panel rather than under it. */
  media?: ReactNode;
  /**
   * Everything read alongside the capture below the summary sentence: findings, an outcome
   * reason, provenance. The summary sentence itself is not part of this: it is derived from
   * `level` and rendered by this component, so every caller gets the same wording (see
   * `biometricConfidenceStatement`).
   */
  children: ReactNode;
  /**
   * The button bar. Rendered in a footer spanning the dialog rather than inside the information
   * column, so its position does not move with the panel's content.
   *
   * **The last node is always against the right edge**, whether you pass one top-level node or
   * several. Pass more than one (a fragment) to put content on both sides of the bar: everything
   * after the first is grouped right, and the first is pushed away from that group to the left.
   * Passing one node therefore leaves the buttons exactly where they sit when a request link is
   * present, rather than sliding them across, which is what a reader moving between customers
   * needs.
   */
  actions?: ReactNode;
  'data-testid'?: string;
};

/**
 * The modal that opens from the biometric card's "View evidence": a header carrying the heading,
 * the biometric meter and the level name on one line, a two-column body with the captures on the
 * left and an information panel on the right, and a footer action bar spanning the dialog under a
 * divider.
 *
 * **Fixed at 1040px, capped to the viewport**, not `fullWidth` with a breakpoint `maxWidth`. The
 * frozen prototype measured a literal `1040px` at every state, so the paper's width is set
 * directly rather than borrowed from MUI's `sm`/`md`/`lg` breakpoint scale, none of which land on
 * 1040. `maxWidth={false}` turns off MUI's own breakpoint-driven sizing so this is the only width
 * rule in effect; `calc(100% - 64px)` keeps the paper's default 32px margin clear on each side on
 * a narrower viewport instead of overflowing it.
 *
 * `scroll="body"` rather than the MUI default `"paper"`. The default caps the panel at the
 * viewport height and scrolls its middle, which puts a scrollbar inside the dialog and hides part
 * of the evidence behind it. Scrolling the container instead keeps the whole panel intact: the
 * captures and the outcome are meant to be read together, and a reader who has to scroll between
 * them is comparing from memory.
 */
export default function BiometricEvidenceDialog({
  open,
  onClose,
  level,
  heading,
  statusOverride,
  media,
  children,
  actions,
  'data-testid': dataTestId,
}: Readonly<TBiometricEvidenceDialogProps>): ReactElement {
  const theme = useTheme();
  // Generated unconditionally, unlike the old `${dataTestId}-title` scheme: an id derived from an
  // optional prop leaves the dialog with no accessible name at all when a caller omits it, and
  // every story happened to pass one, so nothing caught it.
  const titleId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="body"
      maxWidth={false}
      data-testid={dataTestId}
      aria-labelledby={titleId}
      PaperProps={{
        sx: {
          // Measured off the frozen prototype: a literal 1040px, never wider than the viewport
          // once the paper's own 32px margin is cleared on each side. No size token lands on
          // 1040, so this is a measured, untokened literal.
          width: '1040px',
          maxWidth: 'calc(100% - 64px)',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={(t: Theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: t.semantic.spacing.base,
        })}
      >
        <Stack sx={(t: Theme) => ({ gap: t.semantic.spacing['2xs'] })}>
          <Typography variant="h6" id={titleId}>
            {heading}
          </Typography>
          {/* The meter and the level name share this one line in the frozen prototype. The row
              itself is pinned `flexWrap="nowrap"` and the label's text to `whiteSpace: 'nowrap'`
              rather than relying on there simply being enough room: a flex item's default
              `min-width: auto` lets it shrink below its own content width before wrapping, and a
              narrower dialog (or a longer level name from a future locale) would wrap the level
              onto a second line exactly the way the previous build did. */}
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="nowrap"
            sx={(t: Theme) => ({ gap: t.semantic.spacing.xs })}
          >
            {/* 128px (63px per step, 2px gap), matching the frozen prototype exactly: the check
                card's own narrower default (`4xs`, 64px) is half that, sized for a denser card
                layout rather than this header.
                Wrapped in a Box with that same 128px set as an explicit, absolute `width`, not
                `flex: 'none'` or left bare. The meter's own root fills its container via
                `width: 100%`, and every ancestor up to here (this row, the title stack, the
                dialog title) sizes itself from its *children's* content rather than carrying an
                explicit width of its own. A percentage width has no definite value to resolve
                against in that chain, so browsers fall back to treating it as content-sized,
                which collapses the meter to its gap alone. An explicit pixel width here is the
                first definite value in the chain, so `width: 100%` finally has something to
                resolve against. */}
            <Box sx={{ width: theme.semantic.size['3xs'] }}>
              <BiometricConfidenceMeter level={level} maxWidth={theme.semantic.size['3xs']} />
            </Box>
            <Box sx={{ whiteSpace: 'nowrap' }}>
              {statusOverride ? (
                <Typography
                  variant="body2"
                  sx={(t: Theme) => ({ fontWeight: t.semantic.fontWeight.semibold })}
                >
                  {statusOverride}
                </Typography>
              ) : (
                <BiometricConfidenceLabel level={level} />
              )}
            </Box>
          </Stack>
        </Stack>
        <IconButton
          aria-label="Close"
          data-action="close-biometric-evidence-dialog"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={(t: Theme) => ({
          padding: `0 ${t.semantic.spacing['2lg']} ${t.semantic.spacing.lg}`,
        })}
      >
        <Stack direction="row" flexWrap="wrap" sx={(t: Theme) => ({ gap: t.semantic.spacing.lg })}>
          {media}

          <Stack
            sx={(t: Theme) => ({
              // 500px measured off the frozen prototype; no size token lands on it, so this is a
              // literal. `flex: 'none'` keeps it fixed rather than sharing the row's leftover
              // space with the captures column, which sizes to its own content.
              width: 500,
              flex: 'none',
              gap: t.semantic.spacing.base,
            })}
          >
            <Typography variant="body2" color="text.secondary" sx={{ textWrap: 'pretty' }}>
              {biometricConfidenceStatement(level)}
            </Typography>
            {children}
          </Stack>
        </Stack>
      </DialogContent>

      {actions && (
        <>
          <Divider />
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            sx={(t: Theme) => ({
              gap: t.semantic.spacing.xs,
              padding: `${t.semantic.spacing.sm} ${t.semantic.spacing.lg}`,
              // `flex-end` plus an auto margin on the left-hand node, rather than
              // `space-between`. They look identical while the footer holds two nodes, and differ
              // exactly when it holds one: `space-between` aligns a lone child to the *start*, so
              // the buttons slid left whenever the request link was absent, which is most of the
              // time. Anchoring to the end and pushing the left-hand node away from the group
              // instead keeps the buttons still, which is what a reader moving between customers
              // needs.
              justifyContent: 'flex-end',
              '& > :first-child:not(:last-child)': { marginRight: 'auto' },
            })}
          >
            {actions}
          </Stack>
        </>
      )}
    </Dialog>
  );
}
